import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable, firstValueFrom } from 'rxjs';
import { debounceTime, skip, takeUntil, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';

import { CartApiService } from './cart-api.service';
import { CartStateService } from './cart-state.service';
import { AuthenticationService } from '../authentication';
import { TestCustomerService } from '../test-customer/test-customer.service';
import { SnackbarService } from '../utils/snackbar.service';
import { InfoModalComponent } from '../../../shared/components/info-modal/info-modal.component';
import {
  Cart,
  CartProduct,
  DeletedProducts,
} from '../../../shared/models/cart.model';
import { DepartmentWithResidents } from '../../../shared/models/department.model';
import { Package } from '../../../shared/models/package.model';
import { Resident } from '../../../shared/models/resident.model';
import { ShopSelectionStore } from '../../state/shop-selection.store';
import { Action } from '../../types/enums';
import { BackendResponse } from '../../types/interfaces';

/**
 * Facade over the two specialised cart services:
 *  - `CartStateService` owns what is in the basket,
 *  - `CartApiService` talks to the backend.
 *
 * It keeps the public API the rest of the app already uses, and owns the
 * orchestration that needs both halves: debounced auto-save, cleanup of
 * unavailable products and the "may the user shop" check.
 */
@Injectable({ providedIn: 'root' })
export class CartService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private followedDepartmentId: number | null = null;

  constructor(
    private cartState: CartStateService,
    private cartApi: CartApiService,
    private shopSelectionStore: ShopSelectionStore,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
    private authService: AuthenticationService,
    private testCustomerService: TestCustomerService
  ) {
    this.cartState
      .getUpdate$()
      .pipe(skip(1), debounceTime(1000), takeUntil(this.destroy$))
      .subscribe(() => {
        firstValueFrom(this.saveCart(Action.SaveCartButton)).then(() => {
          this.snackbarService.successCart();
        });
      });

    this.cartState
      .getUpdate$()
      .pipe(skip(1), debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => {
        this.cartState.replaceCart(this.cart);
      });

    this.shopSelectionStore.department$
      .pipe(takeUntil(this.destroy$))
      .subscribe(department => this.followDepartment(department));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get departmentId(): number {
    return this.cartState.departmentId;
  }

  set departmentId(id: number) {
    this.cartState.departmentId = id;
  }

  // --- state delegation ---------------------------------------------------

  get cart(): Cart {
    return this.cartState.cart;
  }

  setCart(cart: Cart): void {
    this.cartState.setCart(cart);
  }

  getCart$(): Observable<Cart> {
    return this.cartState.getCart$();
  }

  getCartTotal$(): Observable<number> {
    return this.cartState.getCartTotal$();
  }

  setAllCarts(carts: Cart[] | null): void {
    this.cartState.setAllCarts(carts);
  }

  getAllCarts(): Observable<Cart[]> {
    return this.cartState.getAllCarts();
  }

  replaceCart(newCart: Cart): void {
    this.cartState.replaceCart(newCart);
  }

  setUpdate(): void {
    this.cartState.setUpdate();
  }

  setPreventShoping(value: boolean): void {
    this.cartState.setPreventShoping(value);
  }

  getPreventShoping(): Observable<boolean> {
    return this.cartState.getPreventShoping();
  }

  getActiveDepartment(id: number): void {
    this.cartState.getActiveDepartment(id);
  }

  getCurrentCart(residentId: number) {
    return this.cartState.getCurrentCart(residentId);
  }

  getProductQuantity(residentId: number, productId: number): number {
    return this.cartState.getProductQuantity(residentId, productId);
  }

  getResidentCartProduct(
    residentId: number,
    productId: number
  ): CartProduct | undefined {
    return this.cartState.getResidentCartProduct(residentId, productId);
  }

  setProductQuantity(
    residentId: number,
    productId: number,
    quantity: number
  ): void {
    this.cartState.setProductQuantity(residentId, productId, quantity);
  }

  getNewProduct(productId: number, quantity: number): CartProduct {
    return this.cartState.getNewProduct(productId, quantity);
  }

  checkIfExistsProductOfCurrentCategory(category: string): boolean {
    return this.cartState.checkIfExistsProductOfCurrentCategory(category);
  }

  emptyCarts(departmentId: number): void {
    this.cartState.emptyCarts(departmentId);
  }

  createEmptyCartForDepartmentWithResidents(
    residents: Resident[],
    departmentId: number
  ): Cart {
    return this.cartState.createEmptyCartForDepartmentWithResidents(
      residents,
      departmentId
    );
  }

  writeCartsValue(_cartsValue?: Cart): void {
    this.cartState.writeCartsValue();
  }

  setOrderedCategoriesFromReceivedCart(): void {
    this.cartState.setOrderedCategoriesFromReceivedCart();
  }

  deleteInexistentResidentsCarts(department: DepartmentWithResidents): void {
    this.cartState.deleteInexistentResidentsCarts(department);
  }

  private followDepartment(department: DepartmentWithResidents | null): void {
    const previousDepartmentId = this.followedDepartmentId;
    this.followedDepartmentId = department?.departmentId ?? null;
    if (!department) return;
    const cart = this.cartState.allCarts.find(
      c => c.departmentId === department.departmentId
    );
    this.setCart(cart);
    if (cart && department.departmentId !== previousDepartmentId) {
      this.deleteInexistentResidentsCarts(department);
    }
  }

  // --- data fetching, wired back into the state ---------------------------

  saveCart(action: Action): Observable<BackendResponse<{ id: number }>> {
    return this.cartApi.saveCart(this.cart, action);
  }

  getPackagingPrice(cartId: number): Observable<BackendResponse<Package[]>> {
    return this.cartApi.getPackagingPrice(cartId);
  }

  fetchCart(departmentId: number): Observable<BackendResponse<Cart>> {
    return this.cartApi
      .fetchCart(departmentId)
      .pipe(tap(res => this.setCart(res.data)));
  }

  fetchCustomerCarts(): Observable<BackendResponse<Cart[]>> {
    return this.cartApi.fetchCustomerCarts().pipe(
      tap(carts => {
        this.setAllCarts(carts.data);
        if (this.authService.loggedInAsTestCustomer()) {
          this.testCustomerService.saveCarts(carts.data);
        }
        // The cleanup strips products in place and resolves asynchronously, so
        // re-publish afterwards: without a second emission subscribers keep
        // rendering the products that were just removed.
        void this.deleteUnavailableProducts(carts.data).then(changed => {
          if (changed) {
            this.setAllCarts(carts.data);
          }
        });
      })
    );
  }

  // --- orchestration needing both halves ----------------------------------

  /** Resolves to `true` when at least one product was stripped from a cart. */
  async deleteUnavailableProducts(carts: Cart[]): Promise<boolean> {
    const inactiveResidentsProducts: DeletedProducts[] = [];
    const removedProducts: DeletedProducts[] = [];

    carts.forEach(cart => {
      const residents = cart.residentCarts.map(rc => rc.resident);
      const inactiveProducts = cart.filterInactiveResidentsProducts(residents);
      const products = cart.filterUnavailableProducts(residents);
      if (inactiveProducts.length > 0) {
        inactiveResidentsProducts.push({
          cart,
          department: {
            id: cart.departmentId,
            name: cart.departmentName,
            products: inactiveProducts,
          },
        });
      }
      if (products.length > 0) {
        removedProducts.push({
          cart,
          department: {
            id: cart.departmentId,
            name: cart.departmentName,
            products: products,
          },
        });
      }
    });

    if (inactiveResidentsProducts.length > 0) {
      this.openUnavailableProductsModal(inactiveResidentsProducts, true);
    }
    if (removedProducts.length > 0) {
      this.openUnavailableProductsModal(removedProducts, false);
    }

    const updatedCarts = [];
    for (const removedProduct of removedProducts.concat(
      inactiveResidentsProducts
    )) {
      if (!updatedCarts.find(id => id === removedProduct.cart.departmentId)) {
        updatedCarts.push(removedProduct.cart.departmentId);
        this.setCart(removedProduct.cart);
        await firstValueFrom(
          this.saveCart(Action.SaveCartAfterDeletingProducts)
        );
      }
    }

    return removedProducts.length > 0 || inactiveResidentsProducts.length > 0;
  }

  async shouldPreventShoping() {
    const department = await firstValueFrom(
      this.shopSelectionStore.department$
    );
    const resident = await firstValueFrom(this.shopSelectionStore.resident$);
    return !department || department.departmentId == 0 || !resident;
  }

  private openUnavailableProductsModal(
    products: DeletedProducts[],
    residentInactive: boolean
  ): void {
    this.dialog.open(InfoModalComponent, {
      width: '700px',
      autoFocus: false,
      data: {
        title: 'Nicht mehr verfügbare Produkte',
        message: `Die folgenden Produkte sind aktuell leider nicht mehr lieferbar und wurden daher aus dem gespeicherten Warenkorb für folgende Bewohner:innen entfernt:`,
        products: products,
        ...(residentInactive ? { residentInactive: true } : {}),
      },
    });
  }
}
